import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react';

import { cn } from '@/lib/utils';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  label: string;
  /** Hide the label visually but keep it for screen readers. */
  hideLabel?: boolean;
  /** Validation message. Its presence puts the field into the error state. */
  error?: string;
  /** Guidance shown below the field when there is no error. */
  hint?: string;
  leftIcon?: ReactNode;
  id?: string;
}

/**
 * A labelled text input.
 *
 * The `label` prop is required rather than optional: an unlabelled input is an
 * accessibility defect, and making it a type error is the only reliable way to
 * prevent one. `useId` wires label, hint and error together via `aria-describedby`
 * so assistive technology announces the validation message with the field.
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, hideLabel = false, error, hint, leftIcon, className, id, ...rest },
  ref,
) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const errorId = `${inputId}-error`;
  const hintId = `${inputId}-hint`;

  const describedBy = [error ? errorId : null, !error && hint ? hintId : null]
    .filter(Boolean)
    .join(' ');

  return (
    <div className="flex flex-col gap-1.5">
      <label
        htmlFor={inputId}
        className={cn('text-sm font-medium text-[var(--text-primary)]', hideLabel && 'sr-only')}
      >
        {label}
      </label>

      <div className="relative">
        {leftIcon ? (
          <span
            className="pointer-events-none absolute top-1/2 left-3.5 -translate-y-1/2 text-[var(--text-muted)]"
            aria-hidden="true"
          >
            {leftIcon}
          </span>
        ) : null}

        <input
          ref={ref}
          id={inputId}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy || undefined}
          className={cn(
            'h-11 w-full rounded-xl border bg-[var(--surface-raised)] px-4 text-sm',
            'text-[var(--text-primary)] placeholder:text-[var(--text-muted)]',
            'transition-colors duration-200',
            'focus:border-brand-500 focus:ring-2 focus:ring-brand-500/25 focus:outline-none',
            'disabled:cursor-not-allowed disabled:opacity-60',
            leftIcon && 'pl-10',
            error
              ? 'border-danger-500 focus:border-danger-500 focus:ring-danger-500/25'
              : 'border-[var(--border-subtle)]',
            className,
          )}
          {...rest}
        />
      </div>

      {error ? (
        // role="alert" so the message is announced the moment it appears.
        <p id={errorId} role="alert" className="text-danger-400 text-xs">
          {error}
        </p>
      ) : hint ? (
        <p id={hintId} className="text-xs text-[var(--text-muted)]">
          {hint}
        </p>
      ) : null}
    </div>
  );
});
